import React, { useMemo, useState } from 'react';
import { Col, Row } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import useAuthScope from 'hooks/useAuthScope';
import useAiMetrics from '../hooks/useAiMetrics';
import MetricsControls from '../components/aiPerformance/MetricsControls';
import HealthAlerts from '../components/aiPerformance/HealthAlerts';
import QueueNowSection from '../components/aiPerformance/QueueNowSection';
import ThroughputSection from '../components/aiPerformance/ThroughputSection';
import LatencySection from '../components/aiPerformance/LatencySection';
import QueueWaitSection from '../components/aiPerformance/QueueWaitSection';
import '../components/aiPerformance/AiPerformance.scss';

// LocalStorage keys para persistir preferencias del usuario.
const LS_WINDOW = 'ai-perf:window';
const LS_REFRESH = 'ai-perf:refresh-ms';
const LS_EXPECTED_WORKERS = 'ai-perf:expected-workers';
const LS_TIME_DIM = 'ai-perf:time-dimension';
const LS_TENANT_FILTER = 'ai-perf:filter-tenant';
const LS_SOURCE = 'ai-perf:source';

const readLs = (key, fallback) => {
  try {
    const v = localStorage.getItem(key);
    if (v == null) return fallback;
    if (typeof fallback === 'number') {
      const n = Number(v);
      return isNaN(n) ? fallback : n;
    }
    return v;
  } catch (e) {
    return fallback;
  }
};

const writeLs = (key, value) => {
  try {
    localStorage.setItem(key, String(value));
  } catch (e) {
    // ignored
  }
};

/**
 * Skeleton placeholders para el primer load. Mantienen la estructura visual
 * para evitar layout shift cuando llega la data.
 */
const SkeletonBlock = ({ height = 120 }) => (
  <div
    className="ai-perf-skeleton"
    style={{ height, borderRadius: 8, marginBottom: 12 }}
  />
);

const FirstLoadSkeleton = () => (
  <>
    <Row className="g-3 mb-3">
      {[0, 1, 2, 3].map((i) => (
        <Col xs="12" md="6" lg="3" key={i}>
          <SkeletonBlock height={104} />
        </Col>
      ))}
    </Row>
    <Row className="g-3 mb-3">
      <Col xs="12" lg="5">
        <SkeletonBlock height={320} />
      </Col>
      <Col xs="12" lg="7">
        <SkeletonBlock height={320} />
      </Col>
    </Row>
    <Row className="g-3">
      <Col xs="12" lg="6">
        <SkeletonBlock height={420} />
      </Col>
      <Col xs="12" lg="6">
        <SkeletonBlock height={420} />
      </Col>
    </Row>
  </>
);

const AiPerformanceTab = () => {
  const { t } = useTranslation();
  // Leemos el scope tenant del JWT (no de userData del context). userData
  // depende de GET /users/:id que el backend no expone, asi que cuando
  // falla el filtro tenant no se aplicaba. Ver hooks/useAuthScope.
  const { roleType, companyId, logisticCompanyId } = useAuthScope();
  const [window, setWindow] = useState(() => readLs(LS_WINDOW, '1h'));
  const [from, setFrom] = useState(undefined);
  const [to, setTo] = useState(undefined);
  const [refreshMs, setRefreshMs] = useState(() => readLs(LS_REFRESH, 30000));
  const [expectedWorkers] = useState(() => readLs(LS_EXPECTED_WORKERS, 4));
  const [timeDimension, setTimeDimension] = useState(() => readLs(LS_TIME_DIM, 'finished_at'));
  const [filterTenant, setFilterTenant] = useState(() => readLs(LS_TENANT_FILTER, '1') === '1');
  // Default '' = "Todos los origenes". Otros valores: live | batch | backfill |
  // manual | reconciler (whitelist validada en proxy y upstream).
  const [sourceFilter, setSourceFilter] = useState(() => readLs(LS_SOURCE, ''));

  // Detecta el alcance del usuario para enviarlo al upstream. calidad-ia v2
  // soporta tanto `company_id` (cuentas tipo `company`) como
  // `logistic_company_id` (cuentas tipo `logistic`) como query y devuelve el
  // filtro aplicado en `data.filters.*`. Ver
  // delivery-quality/docs/calidad-ia-metrics-coordination.md.
  const tenantParams = useMemo(() => {
    if (!filterTenant) return {};
    if (roleType === 'logistic' && logisticCompanyId) {
      return { logistic_company_id: logisticCompanyId };
    }
    if (roleType === 'company' && companyId) {
      return { company_id: companyId };
    }
    return {};
  }, [filterTenant, roleType, companyId, logisticCompanyId]);

  const params = useMemo(() => {
    const base = from && to ? { from, to } : { window };
    return {
      ...base,
      ...tenantParams,
      time_dimension: timeDimension,
      source: sourceFilter || undefined,
    };
  }, [from, to, window, tenantParams, timeDimension, sourceFilter]);

  const { data, loading, refreshing, error, lastUpdatedAt, refresh } = useAiMetrics({
    params,
    refreshMs,
  });

  const handleControlsChange = (patch) => {
    if ('window' in patch) {
      setWindow(patch.window);
      if (patch.window) writeLs(LS_WINDOW, patch.window);
    }
    if ('from' in patch) setFrom(patch.from);
    if ('to' in patch) setTo(patch.to);
    if ('refreshMs' in patch) {
      setRefreshMs(patch.refreshMs);
      writeLs(LS_REFRESH, patch.refreshMs);
    }
    if ('timeDimension' in patch) {
      setTimeDimension(patch.timeDimension);
      writeLs(LS_TIME_DIM, patch.timeDimension);
    }
    if ('filterTenant' in patch) {
      setFilterTenant(patch.filterTenant);
      writeLs(LS_TENANT_FILTER, patch.filterTenant ? '1' : '0');
    }
    if ('source' in patch) {
      setSourceFilter(patch.source || '');
      writeLs(LS_SOURCE, patch.source || '');
    }
  };

  // Capacidades del upstream detectadas a partir del shape de la respuesta
  // (calidad-ia v2 las expone). Si no estan, los componentes hijos degradan
  // a la vista clasica basada en jobs.
  const upstreamSupportsTenantFilter =
    !!data?.filters?.company_id ||
    !!data?.filters?.logistic_company_id ||
    !!data?.scope?.company_id ||
    !!data?.scope?.logistic_company_id;
  const upstreamReportedTimeDimension = data?.window?.time_dimension || data?.filters?.time_dimension;
  const upstreamSupportsTimeDimension = !!upstreamReportedTimeDimension;
  const upstreamReportedSource = data?.filters?.source;

  // Si el usuario marco "Solo mi empresa" pero la respuesta NO confirma el
  // filtro aplicado, asumimos que el upstream no lo soporto en este request
  // y mostramos un aviso explicativo (cuentas multi-tenant podrian inflar
  // el total).
  const tenantFilterRequested = filterTenant && Object.keys(tenantParams).length > 0;
  const tenantFilterNotApplied = tenantFilterRequested && data && !upstreamSupportsTenantFilter;

  const isFirstLoad = loading && !data;
  const showStaleBanner = !!error && !!data;

  return (
    <div className="w-full min-w-0 ai-perf-tab">
      <MetricsControls
        window={window}
        from={from}
        to={to}
        refreshMs={refreshMs}
        refreshing={refreshing}
        loading={loading}
        lastUpdatedAt={lastUpdatedAt}
        timeDimension={timeDimension}
        filterTenant={filterTenant}
        source={sourceFilter}
        onChange={handleControlsChange}
        onRefresh={refresh}
      />

      {/* Aviso defensivo: el usuario pidio filtrar por su empresa pero el
          upstream no eco el filtro aplicado en `data.filters.*`. No deberia
          ocurrir con calidad-ia v2; si pasa, indica que el id no matchea
          ningun job en la ventana o que el upstream esta degradado. */}
      {tenantFilterNotApplied && (
        <div
          className="alert alert-warning d-flex align-items-center gap-2 py-2 mb-3"
          role="alert"
          style={{ fontSize: 12 }}
        >
          <i className="fas fa-triangle-exclamation" />
          <div style={{ flex: 1 }}>
            {t('Pediste filtrar por tu empresa pero calidad-ia no devolvio el filtro aplicado en esta respuesta. Puede que no haya jobs de tu empresa en la ventana seleccionada, o que el filtro no se haya aplicado correctamente upstream.')}
          </div>
        </div>
      )}

      {/* Banner si el ultimo intento fallo pero tenemos datos previos */}
      {showStaleBanner && (
        <div
          className="alert alert-danger d-flex align-items-center gap-2 py-2 mb-3"
          role="alert"
          style={{ fontSize: 13 }}
        >
          <i className="fas fa-circle-exclamation" />
          <div style={{ flex: 1 }}>
            <b>{t('No se pudo refrescar')}</b>: {String(error).slice(0, 200)}.
            {t('Mostrando último snapshot conocido.')}
          </div>
        </div>
      )}

      {/* Error sin datos previos: pantalla de error completa */}
      {error && !data && !loading && (
        <div className="alert alert-danger" role="alert">
          <div style={{ fontWeight: 600, marginBottom: 4 }}>
            <i className="fas fa-circle-exclamation me-2" />
            {t('Error al obtener métricas')}
          </div>
          <div style={{ fontSize: 13 }}>{String(error)}</div>
          <div style={{ marginTop: 8 }}>
            <button type="button" className="btn btn-sm btn-outline-danger" onClick={refresh}>
              {t('Reintentar')}
            </button>
          </div>
        </div>
      )}

      {isFirstLoad && <FirstLoadSkeleton />}

      {data && (
        <>
          <HealthAlerts data={data} expectedWorkers={expectedWorkers} />

          <QueueNowSection data={data} expectedWorkers={expectedWorkers} />

          <ThroughputSection data={data} />

          <Row className="g-3">
            <Col xs="12" xl="6">
              <LatencySection data={data} />
            </Col>
            <Col xs="12" xl="6">
              <QueueWaitSection data={data} />
            </Col>
          </Row>

          {data?.window && (
            <div
              className="text-center mt-2"
              style={{ fontSize: 11, color: 'var(--text-tertiary)' }}
            >
              {t('Ventana actual')}: {new Date(data.window.from).toLocaleString()} →{' '}
              {new Date(data.window.to).toLocaleString()}
              {data.window.source && ` · ${t('ventana')}: ${data.window.source}`}
              {upstreamSupportsTimeDimension && ` · ${t('eje')}: ${upstreamReportedTimeDimension}`}
              {upstreamReportedSource && ` · ${t('origen')}: ${upstreamReportedSource}`}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AiPerformanceTab;
